if (!customElements.get('quick-add-to-cart')) {
  const formatQuickAddMoney = (cents, format) => {
    const normalizedCents = typeof cents === 'string' ? cents.replace('.', '') : cents;
    const formatString = format || `\${{amount}}`;
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) return '';

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? `${decimal}${parts[1]}` : '';
      return dollars + centsPart;
    };

    let value = '';
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(normalizedCents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(normalizedCents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(normalizedCents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(normalizedCents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(normalizedCents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(normalizedCents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  };

  customElements.define(
    'quick-add-to-cart',
    class QuickAddToCart extends HTMLElement {
      constructor() {
        super();
        this.bypassModal = false;
        this.modalOpen = false;
        this.pendingSubmitVariantId = undefined;
        this.pendingUpsellVariantId = undefined;
        this.lastSelectedVariant = undefined;
        this._progressiveHandler = undefined;
        this.optionChangeUnsubscriber = undefined;
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productId = this.dataset.productId;
        this.productFormId = this.dataset.productFormId;
        this.hasOnlyDefaultVariant = this.dataset.hasOnlyDefaultVariant === 'true';
        this.variantOptionCount = parseInt(this.dataset.variantOptionCount || '0', 10);
        this.hasUpsellStep = this.dataset.hasUpsellStep === 'true';
        this.moneyFormat = this.dataset.moneyFormat || '';
        this.pinVariantId = this.dataset.pinVariantId || '';
        this.availableVariantCount = parseInt(this.dataset.availableVariantCount || '0', 10);
        this.enableVariantModal = this.dataset.enableVariantModal === 'true';

        this.shouldOpenModal =
          this.enableVariantModal &&
          !this.pinVariantId &&
          this.availableVariantCount > 1 &&
          (!this.hasOnlyDefaultVariant || this.hasUpsellStep) &&
          this.variantOptionCount > 0;

        if (!this.sectionId || !this.productId || !this.productFormId) {
          console.warn('QuickAddToCart: missing required data attributes', {
            sectionId: this.sectionId,
            productId: this.productId,
            productFormId: this.productFormId,
          });
          return;
        }

        this.setupElements();
        this.init();
      }

      disconnectedCallback() {
        this.optionChangeUnsubscriber?.();
        if (this._progressiveHandler && this.variantSelects) {
          this.variantSelects.removeEventListener('click', this._progressiveHandler);
        }
      }

      setupElements() {
        this.cardForm = this.querySelector('form');
        this.cardVariantInput = this.cardForm?.querySelector('.product-variant-id');
        this.cardSubmitButton = this.cardForm?.querySelector('[type="submit"]');
        const modalId = `QuickAddVariantModal-${this.sectionId}-${this.productId}`;
        this.variantModal = document.getElementById(modalId) || this.querySelector(`modal-dialog#${modalId}`);
        this.variantSelects =
          document.getElementById(`variant-selects-${this.sectionId}-${this.productId}-quick-add`) ||
          this.querySelector(`#variant-selects-${this.sectionId}-${this.productId}-quick-add`);
        this.upsellStep = document.getElementById(`QuickAddUpsellStep-${this.sectionId}-${this.productId}`);
        this.upsellSkipButton = document.getElementById(`QuickAddUpsellSkip-${this.sectionId}-${this.productId}`);
        this.modalPrice =
          document.getElementById(`QuickAddPrice-${this.sectionId}-${this.productId}`) ||
          this.variantModal?.querySelector('.quick-add-variant-modal__price');
        this.variantsData = this.getVariantsData();
      }

      getVariantsData() {
        const script = this.variantSelects?.querySelector('[data-product-variants]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.warn('QuickAddToCart: could not parse variants JSON', error);
          return [];
        }
      }

      init() {
        if (!this.cardForm) return;

        if (this.shouldOpenModal) {
          const intercept = (event) => {
            if (this.bypassModal) return;
            event.preventDefault();
            event.stopPropagation();
            this.openVariantModal();
          };

          this.cardForm.addEventListener('submit', intercept, true);
          this.cardSubmitButton?.addEventListener('click', intercept, true);
        }

        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.optionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
            const target = event?.data?.target;
            if (!target || !this.variantSelects?.contains(target)) return;
            this.updateSelectedVariantFromOptions();
          });
        }

        this.setupUpsellListeners();
      }

      getCurrentVariantId() {
        if (this.pendingSubmitVariantId) return this.pendingSubmitVariantId;

        const resolved = this.resolveVariantFromOptions();
        if (resolved?.available) return resolved.id;

        if (this.lastSelectedVariant?.available) return this.lastSelectedVariant.id;

        return this.cardVariantInput?.value || this.pinVariantId || undefined;
      }

      resolveVariantFromOptions(requireAvailable = true) {
        if (!this.variantSelects || !this.variantsData.length) return null;

        const fieldsets = Array.from(this.variantSelects.querySelectorAll('fieldset'));
        const selectedOptions = [];

        fieldsets.forEach((fieldset, index) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked:not(:disabled)');
          if (checked) selectedOptions[index] = checked.value;
        });

        if (selectedOptions.length === 0 || selectedOptions.includes(undefined)) return null;

        return (
          this.variantsData.find((variant) => {
            const optionsMatch = variant.options.every((option, index) => option === selectedOptions[index]);
            return requireAvailable ? optionsMatch && variant.available : optionsMatch;
          }) || null
        );
      }

      updateModalPrice(variant) {
        if (!variant || !this.modalPrice) return;

        const priceRoot = this.modalPrice.querySelector('.price');
        if (!priceRoot) return;

        const onSale = variant.compare_at_price > variant.price;
        const formattedPrice = formatQuickAddMoney(variant.price, this.moneyFormat);
        const formattedCompareAt = formatQuickAddMoney(variant.compare_at_price, this.moneyFormat);

        priceRoot.classList.toggle('price--on-sale', onSale);
        priceRoot.classList.toggle('price--sold-out', !variant.available);

        const regularPrice = priceRoot.querySelector('.price__regular .price-item--regular');
        const salePrice = priceRoot.querySelector('.price__sale .price-item--sale');
        const compareAtPrice = priceRoot.querySelector('.price__sale s.price-item--regular');

        if (regularPrice) regularPrice.textContent = formattedPrice;
        if (salePrice) salePrice.textContent = formattedPrice;
        if (compareAtPrice) compareAtPrice.textContent = formattedCompareAt;
      }

      updateSelectedVariantFromOptions() {
        const displayVariant = this.resolveVariantFromOptions(false);
        if (displayVariant) {
          this.updateModalPrice(displayVariant);
        }

        const variant = this.resolveVariantFromOptions();
        if (!variant) return;

        this.lastSelectedVariant = variant;
        if (this.cardVariantInput) {
          this.cardVariantInput.value = variant.id;
        }
      }

      openVariantModal() {
        this.setupElements();
        if (!this.variantModal) return;

        this.clearUpsellSelection();
        this.pendingSubmitVariantId = undefined;
        this.modalOpen = true;
        this.variantModal.setAttribute('open', '');

        this.setupElements();
        this.setupProgressiveVariantSteps();
        this.setupUpsellListeners();
        this.updateSelectedVariantFromOptions();

        if (typeof this.variantModal.show === 'function') {
          this.variantModal.show(this.cardSubmitButton || this);
        }
      }

      closeVariantModal() {
        if (!this.variantModal) return;

        this.modalOpen = false;
        this.pendingSubmitVariantId = undefined;
        this.pendingUpsellVariantId = undefined;

        if (typeof this.variantModal.hide === 'function') {
          this.variantModal.hide();
        } else {
          this.variantModal.removeAttribute('open');
        }
      }

      setupProgressiveVariantSteps() {
        const upsellStep = this.upsellStep;
        const fieldsets = this.variantSelects ? Array.from(this.variantSelects.querySelectorAll('fieldset')) : [];

        if (upsellStep) {
          upsellStep.classList.add('hidden');
        }

        this.clearUpsellSelection();
        this.pendingSubmitVariantId = undefined;

        if (!fieldsets.length && this.hasUpsellStep) {
          this.showUpsellStep(this.getCurrentVariantId());
          return;
        }

        if (fieldsets.length) {
          fieldsets.forEach((fieldset, index) => {
            fieldset.classList.toggle('hidden', index !== 0);
          });
        }

        const advanceHandler = (event) => {
          const target = event.target;
          if (!target) return;

          const statusClick = target.closest('.quick-add-variant-modal__option-status--available');
          const clickedLabel = target.closest('label');
          const labelFor = clickedLabel?.getAttribute('for');
          const radio = clickedLabel
            ? this.variantSelects?.querySelector(labelFor ? `#${CSS.escape(labelFor)}` : null)
            : target.closest('input[type="radio"]');

          if (!radio || radio.disabled) return;

          const fieldset = radio.closest('fieldset');
          if (!fieldset || !fieldsets.includes(fieldset)) return;

          const index = fieldsets.indexOf(fieldset);
          const total = fieldsets.length;

          if (statusClick) {
            event.preventDefault();
            event.stopPropagation();

            if (!radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
              this.updateSelectedVariantFromOptions();
              return;
            }

            this.updateSelectedVariantFromOptions();
            const resolvedVariant = this.resolveVariantFromOptions();
            if (resolvedVariant?.available) {
              this.handleAddIntent(resolvedVariant.id);
            }
            return;
          }

          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }

          this.updateSelectedVariantFromOptions();

          if (index !== -1 && index + 1 < total) {
            fieldset.classList.add('hidden');
            fieldsets[index + 1].classList.remove('hidden');
            fieldsets[index + 1].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        };

        if (this._progressiveHandler) {
          this.variantSelects?.removeEventListener('click', this._progressiveHandler);
        }

        this._progressiveHandler = advanceHandler;
        this.variantSelects?.addEventListener('click', this._progressiveHandler);
      }

      clearUpsellSelection() {
        this.pendingUpsellVariantId = undefined;
      }

      showUpsellStep(variantId) {
        if (!this.hasUpsellStep || !this.upsellStep) return false;

        this.pendingSubmitVariantId = variantId || this.getCurrentVariantId();
        this.upsellStep.classList.remove('hidden');
        this.variantSelects?.querySelectorAll('fieldset').forEach((fieldset) => {
          fieldset.classList.add('hidden');
        });

        requestAnimationFrame(() => {
          this.upsellStep?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });

        return true;
      }

      handleAddIntent(variantId) {
        if (!variantId) return;
        if (this.showUpsellStep(variantId)) return;
        this.submitSelectedVariant(variantId);
      }

      setupUpsellListeners() {
        if (!this.upsellStep || this.upsellStep.dataset.listenersBound === 'true') return;

        this.upsellStep.dataset.listenersBound = 'true';

        this.upsellStep.addEventListener('click', (event) => {
          const upsellCard = event.target.closest('.quick-add-variant-modal__upsell-card');
          if (!upsellCard) return;

          event.preventDefault();
          this.pendingUpsellVariantId = upsellCard.dataset.upsellVariantId;
          this.submitSelectedVariant(this.getCurrentVariantId(), this.pendingUpsellVariantId);
        });

        this.upsellSkipButton?.addEventListener('click', () => {
          this.clearUpsellSelection();
          this.submitSelectedVariant(this.getCurrentVariantId());
        });
      }

      submitSelectedVariant(variantId, upsellVariantId) {
        if (!variantId) return;

        if (upsellVariantId) {
          this.addItemsToCart([
            { id: variantId, quantity: 1 },
            { id: upsellVariantId, quantity: 1 },
          ]);
          this.closeVariantModal();
          return;
        }

        this.setCardVariantAndSubmit(variantId);
        this.closeVariantModal();
      }

      setCardVariantAndSubmit(variantId) {
        if (!this.cardForm || !variantId) return;

        if (this.cardVariantInput) {
          this.cardVariantInput.value = variantId;
          this.cardVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.cardSubmitButton) {
          this.cardSubmitButton.removeAttribute('aria-disabled');
          this.cardSubmitButton.removeAttribute('disabled');
          this.cardSubmitButton.classList.remove('loading');
        }

        this.bypassModal = true;

        setTimeout(() => {
          if (typeof this.cardForm.requestSubmit === 'function') {
            this.cardForm.requestSubmit();
          } else {
            this.cardForm.submit();
          }

          setTimeout(() => {
            this.bypassModal = false;
          }, 500);
        }, 50);
      }

      addItemsToCart(items) {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        items.forEach((item, index) => {
          formData.append(`items[${index}][id]`, item.id);
          formData.append(`items[${index}][quantity]`, item.quantity);
        });

        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(this.cardSubmitButton || this);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'quick-add-to-cart',
                errors: response.errors || response.description,
                message: response.message,
              });
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-add-to-cart',
              cartData: response,
            });

            if (window.CartPostAddFeedback?.isEnabled?.()) {
              window.CartPostAddFeedback.handleSuccess(response, cart, this.cardSubmitButton);
              return;
            }

            if (cart) {
              cart.renderContents(response);
            } else {
              window.location = window.routes.cart_url;
            }
          })
          .catch((error) => {
            console.error('QuickAddToCart: cart add failed', error);
          });
      }
    }
  );
}
